import React, { useState } from 'react';
import ORBApi from '../../../services/ORBApi';
import {
	Button,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	useTheme,
	Paper,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import ExcelIcon from '../../../icons/ExcelIcon';
import MailIcon from '../../../icons/MailIcon';
import Pagination from '@mui/material/Pagination';
import { Modal, Tooltip } from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import { toast } from 'react-toastify';
import Loading from '../../../components/common/Loading';
import { formatCurrency } from '../../../utils/CurrencyUtils';
import moment from 'moment';

function ChecksumErrorDetails({ checksum }) {
	if (!checksum) {
		return <span>Distribuição falhou. Verifique se há faixas e splits configurados.</span>;
	}
	const delta = checksum.delta_pct != null ? (checksum.delta_pct * 100).toFixed(2) : null;
	const expected = checksum.expected != null ? checksum.expected.toFixed(2) : '-';
	const delivered = checksum.delivered != null ? checksum.delivered.toFixed(2) : '-';
	const isUnder = checksum.delivered < checksum.expected;
	return (
		<div style={{ fontSize: 12, lineHeight: 1.6 }}>
			<div>Possíveis faixas não cadastradas ou sem split ({delta != null ? `${delta}%` : '-'})</div>
		</div>
	);
}

export function DistributionsTable(props) {
	//{data, onPageChange, onClick}
	const theme = useTheme();
	const order = props.order || 'desc';
	const orderBy = props.orderBy || 'reference_date';

	// Mapeamento entre headers e campos dos dados (suporta PT e EN)
	// IMPORTANTE: Os nomes aqui devem corresponder aos campos do backend
	const getFieldName = (headerTitle) => {
		const headerMap = {
			// Português
			'Data de referência': 'reference_date',
			'Nome': 'name',
			'Valor distribuido': null, // Não ordenável
			'Status': 'status',
			'Opções': null, // Não ordenável (botões)
			'Download': null, // Não ordenável (botões)
			// Inglês
			'Reference Date': 'reference_date',
			'Name': 'name',
			'Distributed Value': null, // Não ordenável
			'Status': 'status',
			'Options': null,
		};
		return headerMap[headerTitle] || null;
	};

	// Handler para mudança de ordenação - comunica ao componente pai
	const handleRequestSort = (property) => {
		if (!props.onSortChange) return;
		
		const isAsc = orderBy === property && order === 'asc';
		const newOrder = isAsc ? 'desc' : 'asc';
		const newOrderBy = property;
		
		props.onSortChange(newOrderBy, newOrder);
	};

	return (
		<>
			<TableContainer className='table-container' component={Paper}>
				<Table size='small' className='basic-table'>
					<TableHead className='basic-table-head'>
						<TableRow>
							{props.headers_table.map((item, idx) => {
								const fieldName = getFieldName(item);
								const isSortable = fieldName !== null;
								
								return (
									<TableCell 
										key={idx} 
										align='center'
										style={{ color: '#F5F5F5' }}
										sortDirection={orderBy === fieldName ? order : false}
									>
										{isSortable ? (
											<TableSortLabel
												active={orderBy === fieldName}
												direction={orderBy === fieldName ? order : 'asc'}
												onClick={() => handleRequestSort(fieldName)}
												sx={{
													'&.Mui-active': {
														color: theme.palette.secondary.main,
													},
													'& .MuiTableSortLabel-icon': {
														color: orderBy === fieldName 
															? `${theme.palette.secondary.main} !important` 
															: 'rgba(255, 255, 255, 0.54)',
													},
													'&:hover': {
														color: theme.palette.secondary.light,
													},
													'&:hover .MuiTableSortLabel-icon': {
														color: `${theme.palette.secondary.light} !important`,
													},
													color: orderBy === fieldName 
														? theme.palette.secondary.main 
														: 'inherit',
												}}
											>
												{item}
											</TableSortLabel>
										) : (
											item
										)}
									</TableCell>
								);
							})}
						</TableRow>
					</TableHead>
					<TableBody>
						{props.data.distributions?.length > 0 &&
						props.hasRole([
							'DISTRIBUTIONS_WRITE',
							'DISTRIBUTIONS_READ',
						])
							? props.data.distributions.map(
									(distribution, index) => {
										return (
											<DistributionTableRow
												key={distribution.id ?? index}
												hasRole={props.hasRole}
												messages={props.messages}
												distribution={distribution}
												approve_distribution={
													props.approve_distribution
												}
												onApproval={() => {
													props.onPageChange(
														props.data.current_page
													);
												}}
												onClick={props.onClick}
											/>
										);
									}
							  )
							: null}
					</TableBody>
				</Table>
			</TableContainer>
			<Pagination
				shape='rounded'
				className='table-pagination'
				count={props.data.pages}
				page={props.data.current_page}
				onChange={(e, value) => {
					props.onPageChange(value);
				}}
			/>
		</>
	);
}

function MissingSplitsModal({ distributionId, onClose }) {
	const [page, setPage] = useState(1);
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);

	const loadPage = (p) => {
		setLoading(true);
		ORBApi.getDistributionMissingSplits(distributionId, p, 10)
			.then((res) => {
				setData(res);
				setPage(p);
			})
			.finally(() => setLoading(false));
	};

	React.useEffect(() => { loadPage(1); }, [distributionId]);

	return (
		<Modal open onClose={onClose} slotProps={{ backdrop: { timeout: 300 } }}>
			<div className='modal-container' style={{ width: 600, maxHeight: '80vh', overflowY: 'auto' }}>
				<h6 style={{ textAlign: 'center', marginBottom: 12 }}>Faixas sem splits configurados</h6>
				{loading ? (
					<Loading size='30px' />
				) : data && data.tracks?.length > 0 ? (
					<>
						<TableContainer component={Paper}>
							<Table size='small'>
								<TableHead className='basic-table-head'>
									<TableRow>
										<TableCell style={{ color: '#F5F5F5' }}>ISRC</TableCell>
										<TableCell style={{ color: '#F5F5F5' }}>Título</TableCell>
										<TableCell style={{ color: '#F5F5F5' }}>Selo</TableCell>
										<TableCell style={{ color: '#F5F5F5' }} align='right'>Valor Bruto</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{data.tracks.map((track, idx) => (
										<TableRow key={idx}>
											<TableCell>{track.isrc}</TableCell>
											<TableCell>{track.title}</TableCell>
											<TableCell>{track.label}</TableCell>
											<TableCell align='right'>{track.gross_value?.toFixed(4)}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
						{data.pages > 1 && (
							<Pagination
								shape='rounded'
								className='table-pagination'
								count={data.pages}
								page={data.current_page}
								onChange={(e, v) => loadPage(v)}
								style={{ marginTop: 8 }}
							/>
						)}
					</>
				) : (
					<p style={{ textAlign: 'center' }}>Nenhuma faixa sem split encontrada.</p>
				)}
			</div>
		</Modal>
	);
}

function DistributionTableRow({
	distribution,
	onApproval,
	onClick,
	messages,
	texts,
	approve_distribution,
	hasRole,
}) {
	//{distribution, onApproval}

	const [approving, setApproving] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [sending, setSending] = useState(false);
	const [retrying, setRetrying] = useState(false);
	const [showApproveConfirm, setShowApproveConfirm] = useState(false);
	const [showReproveConfirm, setShowReproveConfirm] = useState(false);
	const [showMissingSplits, setShowMissingSplits] = useState(false);

	const onRetryClickHandler = () => {
		setRetrying(true);
		ORBApi.retryDistribution(distribution.id)
			.then(() => {
				toast.success('Distribuição re-enfileirada para processamento.');
				onApproval();
			})
			.catch(() => toast.error('Erro ao re-enfileirar distribuição.'))
			.finally(() => setRetrying(false));
	};

	const onApproveClickHandler = () => {
		setShowApproveConfirm(true);
	};

	const onReproveClickHandler = () => {
		setShowReproveConfirm(true);
	};

	const onExcelClickHandler = () => {
		setDownloading(true);
		ORBApi.downloadDistribution(distribution.id)
			.then((response) => {
				downloadFile(response);
			})
			.catch(() => toast.error(messages.failed_to_download))
			.finally(() => {
				setDownloading(false);
			});
	};

	const onMailClickHandler = () => {
		setSending(true);
		ORBApi.downloadDistribution(distribution.id, 1)
			.then(() => {
				toast.success(messages.distribution_sent);
			})
			.catch(() => {
				toast.error(messages.distribution_send_error);
			})
			.finally(() => {
				setSending(false);
			});
	};

	function downloadFile(response) {
		const file = new Blob([response.data], {
			type: `application/excel`,
			endings: 'native',
		});
		let fileName =
			response.headers['content-disposition'].split('filename=')[1];
		fileName = fileName.replaceAll('"', '');
		let link = document.createElement('a');
		link.href = window.URL.createObjectURL(file);
		link.download = fileName;
		link.click();
	}

	const closeConfirmBox = () => {
		setShowApproveConfirm(false);
		setShowReproveConfirm(false);
	};
	const doDistributionApproval = (approve) => {
		closeConfirmBox();
		setApproving(true);
		if (approve) {
			ORBApi.approveDistribution(distribution.id)
				.then(() => {
					onApproval();
					toast.success(messages.approved_distribution);
				})
				.catch(() => {
					toast.error(messages.failed_approval_message);
				})
				.finally(() => {
					setApproving(false);
				});
		} else {
			ORBApi.reproveDistribution(distribution.id)
				.then(() => {
					onApproval();
					toast.success(messages.rejected_distribution);
				})
				.catch(() => {
					toast.error(texts.messages.rejection_failed);
				})
				.finally(() => {
					setApproving(false);
				});
		}
	};

	return (
		<TableRow className='row' key={distribution.id}>
			<TableCell align='center' style={{ width: '15%' }}>
				{moment(distribution.reference_date).format('MM/YYYY')}
			</TableCell>
			<TableCell
				align='center'
				style={{ width: '35%' }}
				className={hasRole(['DISTRIBUTIONS_WRITE']) ? 'clickable' : ''}
				onClick={() => {
					if (hasRole(['DISTRIBUTIONS_WRITE'])) onClick(distribution);
				}}
			>
				{distribution.name}
			</TableCell>
			<TableCell style={{ width: '10%' }}>
				{distribution.amount
					? formatCurrency(distribution.amount, distribution.currency)
					: formatCurrency(0, distribution.currency)}
			</TableCell>
			<TableCell align='center' style={{ width: '10%' }}>
				{distribution.checksum && distribution.checksum.status !== 'ok' ? (
					<Tooltip
						title={<ChecksumErrorDetails checksum={distribution.checksum} />}
						arrow
						placement="top"
					>
						<span
							style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
							onClick={() => setShowMissingSplits(true)}
						>
							<ErrorRoundedIcon fontSize="small" color="secondary" />
							{distribution.status}
						</span>
					</Tooltip>
				) : (
					distribution.status
				)}
			</TableCell>
			{hasRole(['DISTRIBUTIONS_WRITE']) ? (
				<TableCell align='center' style={{ width: '20%' }}>
					{distribution.status === 'WaitingApproval' ? (
						approving ? (
							<Loading size='25px' />
						) : (
							<>
								<CheckCircleIcon
									color='secondary'
									className='clickable'
									onClick={onApproveClickHandler}
								/>
								<CancelIcon
									color='secondary'
									className='clickable'
									onClick={onReproveClickHandler}
								/>
							</>
						)
					) : distribution.status === 'Pending' ? (
						retrying ? (
							<Loading size='25px' />
						) : (
							<ReplayIcon
								color='secondary'
								className='clickable'
								titleAccess='Re-processar distribuição'
								onClick={onRetryClickHandler}
							/>
						)
					) : null}
				</TableCell>
			) : (
				<TableCell></TableCell>
			)}

			<TableCell align='center' style={{ width: '15%' }}>
				{distribution.status === 'Approved' ||
				distribution.status === 'WaitingApproval' ? (
					downloading ? (
						<Loading size='15px' />
					) : (
						<ExcelIcon
							className='clickable action-icon'
							onClick={onExcelClickHandler}
						/>
					)
				) : null}
				{distribution.status === 'Approved' ||
				distribution.status === 'WaitingApproval' ? (
					sending ? (
						<Loading size='15px' />
					) : (
						<MailIcon
							className='clickable action-icon'
							onClick={onMailClickHandler}
						/>
					)
				) : null}
			</TableCell>
			{showMissingSplits && (
				<MissingSplitsModal
					distributionId={distribution.id}
					onClose={() => setShowMissingSplits(false)}
				/>
			)}
			{/** Confirm modal for approving / reproving distributions */}
			<Modal
				slotProps={{ backdrop: { timeout: 500 } }}
				open={showApproveConfirm || showReproveConfirm}
				onClose={closeConfirmBox}
			>
				<div className='modal-container confirm'>
					<h6 style={{ textAlign: 'center' }}>
						{showApproveConfirm
							? approve_distribution.confirm_message
							: approve_distribution.refuse_message}
					</h6>
					<div style={{ textAlign: 'center' }}>
						{distribution.name}
					</div>
					<div className='approval-btn-container'>
						<Button
							className='btn-approve'
							variant='contained'
							color='secondary'
							onClick={() =>
								doDistributionApproval(showApproveConfirm)
							}
						>
							{approve_distribution.confirm}
						</Button>
						<Button
							className='btn-reprove'
							variant='contained'
							color='secondary'
							onClick={closeConfirmBox}
						>
							{approve_distribution.cancel}
						</Button>
					</div>
				</div>
			</Modal>
		</TableRow>
	);
}

export function PaymentFilesTable(props) {
	// Componente totalmente controlado - usa props.selectedFileIds (array)
	const handleFileSelect = (fileId) => {
		if (props.onFileSelect) {
			props.onFileSelect(fileId);
		}
	};

	return (
		<>
			<TableContainer>
				<Table size='small' className='basic-table'>
					<TableHead className='basic-table-head'>
						<TableRow>
							<TableCell style={{ width: '50px', padding: '8px' }}>Selecionar</TableCell>
							{props.header_table.map((title, idx) => (
								<TableCell key={idx}>{title}</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{props.data?.reports?.map((pmtFile, index) => {
								return (
									<TableRow key={pmtFile.id ?? index}>
										<TableCell style={{ width: '50px', padding: '8px' }}>
											<input
												type="checkbox"
												value={pmtFile.id}
												checked={props.selectedFileIds && props.selectedFileIds.includes(pmtFile.id)}
												onChange={() => handleFileSelect(pmtFile.id)}
												style={{ cursor: 'pointer' }}
											/>
										</TableCell>
										<TableCell>{pmtFile.date}</TableCell>
										<TableCell>{pmtFile.origin}</TableCell>
										<TableCell>{pmtFile.type}</TableCell>
									</TableRow>
								);
							}) || []}
					</TableBody>
				</Table>
			</TableContainer>
			<Pagination
				shape='rounded'
				className='table-pagination'
				count={props.data.pages}
				page={props.data.current_page}
				onChange={(e, value) => {
					props.onPageChange(value);
				}}
			/>
		</>
	);
}
